// src/pages/Directory.tsx
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  FiMail,
  FiMapPin,
  FiPhone,
  FiSearch,
  FiSmartphone,
  FiX,
} from "react-icons/fi";
import { FaAddressBook, FaFilter } from "react-icons/fa";

import Spinner from "../components/Spinner";
import ErrorPage from "../components/ErrorPage";
import Modal from "../components/Modal";
import Badge from "../components/Badge";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import {
  adminSearchDirectory,
  adminUpdateDirectoryProfile,
} from "../api/directory";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { useCan } from "../authz/useCan";

type EntityId = string | number;

type DirectoryRow = {
  id?: EntityId;
  parentId?: EntityId;
  displayName?: string | null;
  published?: boolean;
  email?: string | null;
  phoneMobile?: string | null;
  phoneOther?: string | null;
  addressStreet?: string | null;
  addressPostalCode?: string | null;
  addressCity?: string | null;
};

type DirectoryPage = {
  content?: DirectoryRow[];
  totalPages?: number;
};

type PageError = {
  message?: string;
  status?: number;
};

type DirectoryForm = {
  published: boolean;
  shareEmail: boolean;
  shareMobile: boolean;
  sharePhoneOther: boolean;
  shareAddress: boolean;
};

type FetchPageOptions = {
  pageToLoad?: number;
  query?: string;
  reset?: boolean;
};

type DataChipProps = {
  ok: boolean;
  title: string;
  children: ReactNode;
};

const PAGE_SIZE = 50;

const hasAddress = (row: DirectoryRow): boolean =>
  Boolean(
    row.addressStreet ||
      row.addressPostalCode ||
      row.addressCity
  );

const PublishedBadge = ({
  published,
  t,
}: {
  published: boolean;
  t: ReturnType<typeof useTranslation>["t"];
}) =>
  published ? (
    <Badge
      tone="success"
      title={t("directory.published", {
        defaultValue: "Published",
      })}
    >
      {t("directory.published", {
        defaultValue: "Published",
      })}
    </Badge>
  ) : (
    <Badge
      tone="neutral"
      title={t("directory.not_published", {
        defaultValue: "Not published",
      })}
    >
      {t("directory.not_published", {
        defaultValue: "Not published",
      })}
    </Badge>
  );

const DataChip = ({
  ok,
  title,
  children,
}: DataChipProps) => (
  <span
    className={[
      "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold",
      ok
        ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
        : "border-slate-200 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400",
    ].join(" ")}
    title={title}
  >
    {children}
  </span>
);

const DataCell = ({ row }: { row: DirectoryRow }) => {
  const emailOk = Boolean(row.email);
  const phoneOk = Boolean(
    row.phoneMobile || row.phoneOther
  );
  const addressOk = hasAddress(row);
  const address = [
    row.addressStreet,
    row.addressPostalCode,
    row.addressCity,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="flex flex-wrap gap-1.5">
      <DataChip
        ok={emailOk}
        title={emailOk ? String(row.email) : "No email"}
      >
        <FiMail aria-hidden="true" />
        Email
      </DataChip>

      <DataChip
        ok={phoneOk}
        title={
          phoneOk
            ? [row.phoneMobile, row.phoneOther]
                .filter(Boolean)
                .join(" / ")
            : "No phone"
        }
      >
        <FiPhone aria-hidden="true" />
        Phone
      </DataChip>

      <DataChip
        ok={addressOk}
        title={addressOk ? address : "No address"}
      >
        <FiMapPin aria-hidden="true" />
        Address
      </DataChip>
    </div>
  );
};

const Toggle = ({
  checked,
  disabled,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}) => (
  <label
    className={[
      "inline-flex items-center",
      disabled
        ? "cursor-not-allowed opacity-50"
        : "cursor-pointer",
    ].join(" ")}
  >
    <input
      type="checkbox"
      role="switch"
      aria-label={ariaLabel}
      aria-checked={checked}
      className="peer sr-only"
      checked={checked}
      disabled={disabled}
      onChange={(event) =>
        onChange(event.target.checked)
      }
    />
    <span className="relative inline-flex h-6 w-11 items-center rounded-full bg-slate-300 transition peer-checked:bg-indigo-600 peer-focus-visible:ring-4 peer-focus-visible:ring-indigo-100 before:absolute before:left-1 before:top-1 before:h-4 before:w-4 before:rounded-full before:bg-white before:shadow-sm before:transition-transform peer-checked:before:translate-x-5 dark:bg-slate-700 dark:peer-focus-visible:ring-indigo-950" />
  </label>
);

export default function Directory() {
  const { t } = useTranslation();
  const canManageDirectory = useCan(
    "directory",
    "manage"
  );

  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] =
    useState("");
  const [rows, setRows] = useState<DirectoryRow[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoading, setIsInitialLoading] =
    useState(true);
  const [loadingMore, setLoadingMore] =
    useState(false);
  const [error, setError] =
    useState<PageError | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(
    null
  );

  const [editing, setEditing] =
    useState<DirectoryRow | null>(null);
  const [form, setForm] =
    useState<DirectoryForm>({
      published: false,
      shareEmail: false,
      shareMobile: false,
      sharePhoneOther: false,
      shareAddress: false,
    });
  const [saving, setSaving] = useState(false);

  const fetchPage = useCallback(
    async ({
      pageToLoad = 0,
      query: searchQuery = "",
      reset = false,
    }: FetchPageOptions = {}) => {
      const isFirstPage =
        pageToLoad === 0 || reset;

      setError(null);

      if (isFirstPage) {
        setIsInitialLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const response =
          (await adminSearchDirectory({
            q: searchQuery.trim(),
            page: pageToLoad,
            size: PAGE_SIZE,
            by: "parent",
          })) as DirectoryPage;

        const content = Array.isArray(
          response?.content
        )
          ? response.content
          : [];

        setRows((current) =>
          isFirstPage
            ? content
            : [...current, ...content]
        );

        const totalPages =
          typeof response?.totalPages === "number"
            ? response.totalPages
            : pageToLoad +
              (content.length === PAGE_SIZE ? 2 : 1);

        setHasMore(
          pageToLoad + 1 < totalPages
        );
        setPage(pageToLoad);

        if (isFirstPage) {
          setAppliedQuery(searchQuery);
        }
      } catch (caughtError) {
        setError(
          mapHttpErrorToMessage(caughtError, t)
        );
      } finally {
        if (isFirstPage) {
          setIsInitialLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [t]
  );

  useEffect(() => {
    void fetchPage({
      pageToLoad: 0,
      query: "",
      reset: true,
    });
  }, [fetchPage]);

  useEffect(() => {
    if (!hasMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          !loadingMore &&
          !isInitialLoading
        ) {
          void fetchPage({
            pageToLoad: page + 1,
            query: appliedQuery,
          });
        }
      },
      { rootMargin: "200px" }
    );

    const node = sentinelRef.current;

    if (node) {
      observer.observe(node);
    }

    return () => observer.disconnect();
  }, [
    appliedQuery,
    fetchPage,
    hasMore,
    isInitialLoading,
    loadingMore,
    page,
  ]);

  const handleSearch = (
    event: FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    void fetchPage({
      pageToLoad: 0,
      query: query.trim(),
      reset: true,
    });
  };

  const openManage = (row: DirectoryRow) => {
    setEditing(row);
    setForm({
      published: Boolean(row.published),
      shareEmail: Boolean(row.email),
      shareMobile: Boolean(row.phoneMobile),
      sharePhoneOther: Boolean(row.phoneOther),
      shareAddress: hasAddress(row),
    });
  };

  const saveManage = async () => {
    if (!editing?.parentId) return;

    setSaving(true);
    setError(null);

    try {
      await adminUpdateDirectoryProfile(
        editing.parentId,
        {
          published: form.published,
          share: {
            email: form.shareEmail,
            phoneMobile: form.shareMobile,
            phoneOther: form.sharePhoneOther,
            address: form.shareAddress,
          },
        }
      );

      await fetchPage({
        pageToLoad: page,
        query: appliedQuery,
        reset: page === 0,
      });

      setEditing(null);
    } catch (caughtError) {
      setError(
        mapHttpErrorToMessage(caughtError, t)
      );
    } finally {
      setSaving(false);
    }
  };

  if (!canManageDirectory) {
    return (
      <ErrorPage
        status={403}
        message={t("error.missing_role", {
          defaultValue:
            "Missing required role: directory:manage",
        })}
      />
    );
  }

  return (
    <main className="lds-page-enter px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1400px] rounded-[32px] border border-slate-200/80 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-950 sm:p-5">
        <div className="space-y-6">
          <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-6 py-6 shadow-sm dark:border-indigo-900 dark:from-indigo-950/50 dark:via-slate-950 dark:to-sky-950/40 sm:px-8">
            <FaAddressBook
              aria-hidden="true"
              className="pointer-events-none absolute right-10 top-1/2 hidden -translate-y-1/2 text-[7rem] text-indigo-200 dark:text-indigo-900/40 sm:block"
            />

            <div className="relative z-10 flex min-w-0 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-3xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
                <FaAddressBook aria-hidden="true" />
              </div>

              <div className="min-w-0 max-w-3xl">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-indigo-700 dark:text-indigo-300">
                  {t("tools.overview.category", {
                    defaultValue: "Tools",
                  })}
                </p>
                <h1 className="mt-1 text-3xl font-bold text-slate-950 dark:text-white">
                  {t(
                    "parentPortal.directory.admin_title",
                    {
                      defaultValue:
                        "Directory administration",
                    }
                  )}
                </h1>
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                  {t(
                    "directory.admin_description",
                    {
                      defaultValue:
                        "Manage directory publication and shared contact information.",
                    }
                  )}
                </p>
              </div>
            </div>
          </section>

          <section className="overflow-visible rounded-[24px] border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-center gap-3 border-b border-slate-200/80 px-5 py-5 dark:border-slate-800 lg:px-6">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
                <FaFilter aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-950 dark:text-white">
                  {t("directory.search_title", {
                    defaultValue:
                      "Search directory",
                  })}
                </h2>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {t(
                    "directory.search_description",
                    {
                      defaultValue:
                        "Find a parent and manage the published directory data.",
                    }
                  )}
                </p>
              </div>
            </div>

            <form
              className="flex flex-col gap-3 px-5 py-5 sm:flex-row lg:px-6"
              onSubmit={handleSearch}
            >
              <div className="relative min-w-0 flex-1">
                <FiSearch
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <input
                  value={query}
                  onChange={(event) =>
                    setQuery(event.target.value)
                  }
                  placeholder={t(
                    "directory.search_placeholder",
                    {
                      defaultValue:
                        "Search by parent name…",
                    }
                  )}
                  className="h-12 w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-10 text-sm font-medium text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                />

                {query ? (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("");
                      void fetchPage({
                        pageToLoad: 0,
                        query: "",
                        reset: true,
                      });
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-indigo-600 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:hover:bg-slate-800 dark:hover:text-indigo-300 dark:focus:ring-indigo-950"
                    aria-label={t("common.clear", {
                      defaultValue:
                        "Clear search field",
                    })}
                  >
                    <FiX aria-hidden="true" />
                  </button>
                ) : null}
              </div>

              <button
                type="submit"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus:ring-indigo-950"
              >
                <FiSearch aria-hidden="true" />
                {t("common.search", {
                  defaultValue: "Search",
                })}
              </button>
            </form>
          </section>

          {isInitialLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : error ? (
            <ErrorPage
              message={error.message}
              status={error.status}
              onRetry={() =>
                void fetchPage({
                  pageToLoad: page,
                  query: appliedQuery,
                  reset: page === 0,
                })
              }
            />
          ) : (
            <>
              <LdsTableCard>
                <div className="border-b border-slate-200/80 px-5 py-4 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  {t("directory.results_count", {
                    defaultValue:
                      "{{count}} directory entries",
                    count: rows.length,
                  })}
                </div>

                <LdsTableScroll>
                  <LdsTable className="min-w-[860px]">
                    <LdsTableHead>
                      <LdsTableRow>
                        <LdsTableHeadCell>
                          {t("parentPortal.name", {
                            defaultValue: "Name",
                          })}
                        </LdsTableHeadCell>
                        <LdsTableHeadCell>
                          {t("common.status", {
                            defaultValue: "Status",
                          })}
                        </LdsTableHeadCell>
                        <LdsTableHeadCell>
                          {t("common.data", {
                            defaultValue: "Data",
                          })}
                        </LdsTableHeadCell>
                        <LdsTableHeadCell>
                          {t(
                            "common.actions.name",
                            {
                              defaultValue: "Actions",
                            }
                          )}
                        </LdsTableHeadCell>
                      </LdsTableRow>
                    </LdsTableHead>

                    <LdsTableBody>
                      {rows.length > 0 ? (
                        rows.map((row) => (
                          <LdsTableRow
                            key={String(
                              row.parentId ??
                                row.id ??
                                row.displayName
                            )}
                          >
                            <LdsTableCell className="font-semibold text-slate-950 dark:text-white">
                              {row.displayName || "—"}
                            </LdsTableCell>

                            <LdsTableCell>
                              <PublishedBadge
                                published={Boolean(
                                  row.published
                                )}
                                t={t}
                              />
                            </LdsTableCell>

                            <LdsTableCell>
                              <DataCell row={row} />
                            </LdsTableCell>

                            <LdsTableCell>
                              <button
                                type="button"
                                onClick={() =>
                                  openManage(row)
                                }
                                className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-indigo-200 dark:hover:border-indigo-600 dark:hover:bg-indigo-950/40 dark:focus:ring-indigo-950"
                              >
                                {t(
                                  "common.actions.manage",
                                  {
                                    defaultValue: "Manage",
                                  }
                                )}
                              </button>
                            </LdsTableCell>
                          </LdsTableRow>
                        ))
                      ) : (
                        <LdsTableRow>
                          <LdsTableCell
                            colSpan={4}
                            className="py-10 text-center text-slate-500 dark:text-slate-400"
                          >
                            {t(
                              "common.no_results",
                              {
                                defaultValue:
                                  "No results",
                              }
                            )}
                          </LdsTableCell>
                        </LdsTableRow>
                      )}
                    </LdsTableBody>
                  </LdsTable>
                </LdsTableScroll>
              </LdsTableCard>

              <div
                ref={sentinelRef}
                className="h-8 w-full"
              />

              {loadingMore ? (
                <div className="flex items-center justify-center gap-2 py-4 text-sm text-slate-500 dark:text-slate-400">
                  <Spinner />
                  {t("common.loading", {
                    defaultValue: "Loading…",
                  })}
                </div>
              ) : null}
            </>
          )}

          <Modal
            show={Boolean(editing)}
            title={t("common.actions.manage", {
              defaultValue: "Manage",
            })}
            onClose={() => setEditing(null)}
            hideDefaultActions
            type="manage"
          >
            {editing ? (
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t(
                      "parentPortal.directory.admin_title",
                      {
                        defaultValue:
                          "Directory administration",
                      }
                    )}
                  </p>
                  <h3 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                    {editing.displayName || "—"}
                  </h3>
                </div>

                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
                  <div>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                      {t(
                        "parentPortal.directory.listed",
                        {
                          defaultValue: "Published",
                        }
                      )}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      {t(
                        "directory.published_description",
                        {
                          defaultValue:
                            "Show this parent in the directory.",
                        }
                      )}
                    </p>
                  </div>

                  <Toggle
                    checked={form.published}
                    ariaLabel={t(
                      "parentPortal.directory.listed",
                      {
                        defaultValue: "Published",
                      }
                    )}
                    onChange={(checked) =>
                      setForm((current) => ({
                        ...current,
                        published: checked,
                      }))
                    }
                  />
                </div>

                <div className="border-t border-slate-200 pt-4 dark:border-slate-800">
                  <h4 className="text-sm font-bold text-slate-950 dark:text-white">
                    {t(
                      "parentPortal.directory.share_title",
                      {
                        defaultValue:
                          "Choose what to share",
                      }
                    )}
                  </h4>

                  <div className="mt-3 space-y-2">
                    {[
                      {
                        key: "shareEmail" as const,
                        label: t(
                          "directory.labels.email",
                          {
                            defaultValue: "Email",
                          }
                        ),
                        icon: <FiMail />,
                        preview: editing.email,
                      },
                      {
                        key: "shareMobile" as const,
                        label: t(
                          "directory.labels.mobile",
                          {
                            defaultValue: "Mobile",
                          }
                        ),
                        icon: <FiSmartphone />,
                        preview:
                          editing.phoneMobile,
                      },
                      {
                        key:
                          "sharePhoneOther" as const,
                        label: t(
                          "directory.labels.phone",
                          {
                            defaultValue: "Phone",
                          }
                        ),
                        icon: <FiPhone />,
                        preview:
                          editing.phoneOther,
                      },
                      {
                        key:
                          "shareAddress" as const,
                        label: t(
                          "directory.labels.address",
                          {
                            defaultValue: "Address",
                          }
                        ),
                        icon: <FiMapPin />,
                        preview: [
                          editing.addressStreet,
                          editing.addressPostalCode,
                          editing.addressCity,
                        ]
                          .filter(Boolean)
                          .join(" "),
                      },
                    ].map(
                      ({
                        key,
                        label,
                        icon,
                        preview,
                      }) => {
                        const hasValue =
                          Boolean(preview);

                        return (
                          <div
                            key={key}
                            className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-3 dark:border-slate-800"
                            title={
                              hasValue
                                ? String(preview)
                                : "No data available"
                            }
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              <span className="text-indigo-600 dark:text-indigo-300">
                                {icon}
                              </span>
                              <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                {label}
                              </span>
                            </div>

                            <Toggle
                              checked={form[key]}
                              disabled={!hasValue}
                              ariaLabel={String(label)}
                              onChange={(checked) =>
                                setForm((current) => ({
                                  ...current,
                                  [key]: checked,
                                }))
                              }
                            />
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() =>
                      setEditing(null)
                    }
                    disabled={saving}
                    className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:focus:ring-indigo-950"
                  >
                    {t("common.cancel", {
                      defaultValue: "Cancel",
                    })}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      void saveManage()
                    }
                    disabled={saving}
                    className="inline-flex h-11 items-center justify-center rounded-xl bg-indigo-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus:ring-indigo-950"
                  >
                    {saving
                      ? t("common.saving", {
                          defaultValue:
                            "Saving…",
                        })
                      : t("common.save", {
                          defaultValue: "Save",
                        })}
                  </button>
                </div>
              </div>
            ) : null}
          </Modal>
        </div>
      </div>
    </main>
  );
}
